/**
 * JotAuditPage Component
 *
 * Main page for the JotAudit dashboard with tab navigation
 * Tabs: Overview | Providers | Notes | Rules
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserRoles } from '@features/user';
import {
  Assessment as OverviewIcon,
  Clear as ClearIcon,
  Description as NotesIcon,
  FilterList as FilterIcon,
  Lock as LockIcon,
  People as ProvidersIcon,
  Receipt as ClaimsIcon,
  Rule as RulesIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { hasRole } from '@utils/roleUtils';

import { DashboardOverview } from './components/dashboard/DashboardOverview';
import {
  DEFAULT_RULES_FILTERS,
  RulesFilterPopover,
  type RulesFilters,
} from './components/filters';
import { NoteAuditsList } from './components/notes/NoteAuditsList';
import { ProvidersList } from './components/providers/ProvidersList';
import { RulesTabContent } from './components/rules/RulesTabContent';
import { TAB_CONFIG } from './constants';
import type { JotAuditTabType } from './types';

const DEFAULT_TAB: JotAuditTabType = 'providers';

/**
 * Configuration for which tabs support search functionality.
 * Tabs not in this list will show the search bar but it won't affect their content.
 */
const SEARCHABLE_TABS: JotAuditTabType[] = ['rules'];

/**
 * Configuration for which tabs support filter functionality.
 */
const FILTERABLE_TABS: JotAuditTabType[] = ['rules'];

interface TabPanelProps {
  children?: React.ReactNode;
  value: JotAuditTabType;
  activeTab: JotAuditTabType;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, activeTab }) => {
  if (value !== activeTab) {
    return null;
  }

  return <Box sx={{ pt: 3 }}>{children}</Box>;
};

export const JotAuditPage: React.FC = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const userRoles = useUserRoles();
  const { jotauditDashboard } = useEventTracking();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter state for each tab (persists across tab switches)
  const [rulesFilters, setRulesFilters] = useState<RulesFilters>(DEFAULT_RULES_FILTERS);

  // Filter popover state
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isFilterOpen = Boolean(filterAnchorEl);

  const isPracticeAdmin = hasRole(userRoles, 'practice_administrator');

  // Get current tab from URL or default
  const currentTab = useMemo<JotAuditTabType>(() => {
    const tab = searchParams.get('tab');
    if (tab && ['providers', 'notes', 'rules', 'claims'].includes(tab)) {
      return tab as JotAuditTabType;
    }
    return DEFAULT_TAB;
  }, [searchParams]);

  // Check if current tab supports search
  const isSearchableTab = SEARCHABLE_TABS.includes(currentTab);

  // Check if current tab supports filters
  const isFilterableTab = FILTERABLE_TABS.includes(currentTab);

  // Count active filters for badge
  const activeFilterCount = useMemo(() => {
    if (currentTab !== 'rules') return 0;

    let count = 0;
    if (rulesFilters.scope !== DEFAULT_RULES_FILTERS.scope) count++;
    if (rulesFilters.ruleType !== DEFAULT_RULES_FILTERS.ruleType) count++;
    if (rulesFilters.showDisabled !== DEFAULT_RULES_FILTERS.showDisabled) count++;
    return count;
  }, [currentTab, rulesFilters]);

  // Track page viewed on mount
  const pageViewedRef = useRef(false);
  useEffect(() => {
    if (isPracticeAdmin && !pageViewedRef.current) {
      pageViewedRef.current = true;
      jotauditDashboard.pageViewed({
        current_tab: currentTab,
      });
    }
  }, [isPracticeAdmin, currentTab, jotauditDashboard]);

  // Clear search when switching tabs to avoid confusion
  const previousTabRef = useRef(currentTab);
  useEffect(() => {
    if (previousTabRef.current !== currentTab) {
      setSearchQuery('');
      previousTabRef.current = currentTab;
    }
  }, [currentTab]);

  // Handle tab change
  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: JotAuditTabType) => {
      // Track tab change
      jotauditDashboard.tabChanged({
        previous_tab: currentTab,
        new_tab: newValue,
      });
      setSearchParams({ tab: newValue });
    },
    [setSearchParams, currentTab, jotauditDashboard],
  );

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  // Handle clearing search
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  // Handle filter popover
  const handleFilterClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  }, []);

  const handleFilterClose = useCallback(() => {
    setFilterAnchorEl(null);
  }, []);

  // Handle rules filter changes
  const handleRulesFiltersChange = useCallback((newFilters: RulesFilters) => {
    setRulesFilters(newFilters);
  }, []);

  // Restrict access to practice administrators
  if (!isPracticeAdmin) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <LockIcon sx={{ fontSize: 48, color: theme.palette.warning.main, mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Access restricted
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
            Only practice administrators can view the Audit Center right now.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Page Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 0.5,
            }}
          >
            Audit Center
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
            Track performance, billing efficiency, and opportunities for improvement
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            placeholder={isSearchableTab ? 'Search rules...' : 'Search not available for this tab'}
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            disabled={!isSearchableTab}
            sx={{
              width: 300,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: 'background.paper',
              },
              // Dim the search bar when not applicable
              opacity: isSearchableTab ? 1 : 0.6,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color={isSearchableTab ? 'action' : 'disabled'} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch} edge="end">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton
            onClick={handleFilterClick}
            disabled={!isFilterableTab}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px',
              p: 1,
              opacity: isFilterableTab ? 1 : 0.6,
            }}
          >
            <Badge
              badgeContent={activeFilterCount}
              color="primary"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.65rem',
                  height: 16,
                  minWidth: 16,
                },
              }}
            >
              <FilterIcon fontSize="small" color={isFilterableTab ? 'action' : 'disabled'} />
            </Badge>
          </IconButton>
        </Box>
      </Box>

      {/* Filter Popover */}
      <Popover
        open={isFilterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: theme.shadows[8],
            },
          },
        }}
      >
        {currentTab === 'rules' && (
          <RulesFilterPopover
            filters={rulesFilters}
            onFiltersChange={handleRulesFiltersChange}
            onClose={handleFilterClose}
          />
        )}
      </Popover>

      {/* Tabs Navigation */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'transparent',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: 'auto',
              px: 3,
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
          }}
        >
          <Tab value={TAB_CONFIG.providers.value} label={TAB_CONFIG.providers.label} />
          <Tab value={TAB_CONFIG.rules.value} label={TAB_CONFIG.rules.label} />
          <Tab value={TAB_CONFIG.notes.value} label={TAB_CONFIG.notes.label} />
          <Tab value={TAB_CONFIG.claims.value} label={TAB_CONFIG.claims.label} />
        </Tabs>
      </Paper>

      {/* Tab Content Container */}
      <Box>
        {/* Clinicians Tab */}
        <TabPanel value="providers" activeTab={currentTab}>
          <ProvidersList />
        </TabPanel>

        {/* Rules Tab */}
        <TabPanel value="rules" activeTab={currentTab}>
          <RulesTabContent searchQuery={searchQuery} filters={rulesFilters} />
        </TabPanel>

        {/* Notes Tab */}
        <TabPanel value="notes" activeTab={currentTab}>
          <NoteAuditsList />
        </TabPanel>

        {/* Claims Tab placeholder */}
        <TabPanel value="claims" activeTab={currentTab}>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Claims monitoring coming soon</Typography>
          </Box>
        </TabPanel>
      </Box>
    </Container>
  );
};

export default JotAuditPage;
